import { Injectable } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { ApiService, AuthService, config } from '@fgb/core';
import { ProviderName } from './models/provider.enum';
import { InputField } from './models/fusion-input.model';

const FUSION_SSO_STATE_COOKIE = 'fusion_sso_state';

@Injectable({
  providedIn: 'root',
})
export class FusionAuthService {
  constructor(private authService: AuthService, private api: ApiService, private cookieService: CookieService) {}

  login(successredirectPath: string = '/fa-callback', clubId: number = 1) {
    let loginUrlParameters: InputField[] = [];

    let redirectUrlParam: InputField = {
      FieldName: 'RedirectUrl',
      Value: `${config.portalUrl}${successredirectPath}`,
    };
    loginUrlParameters.push(redirectUrlParam);
    const state = Math.random().toString(36).substring(2, 9);
    let ssoModeParam: InputField = {
      FieldName: 'State',
      Value: state,
    };
    this.cookieService.set(FUSION_SSO_STATE_COOKIE, state, undefined, '/');
    loginUrlParameters.push(ssoModeParam);

    return this.api.post(`sso/JWT/GenerateUrl/${ProviderName.FusionAuth}/${clubId}`, loginUrlParameters).pipe(
      tap((generatedUrl: any) => {
        window.location.href = generatedUrl;
      })
    );
  }

  /**
   * This method is used to authenticate a token
   * that comes back from the fusion login page
   * and log the user in via the tokens returned
   * from the response.
   * @param jwtToken JWT Token returned from
   * fusion, to be authenticated and
   * passed to given endpoint.
   * @param clubId clubId to be passed to endpoint,
   * default to 1.
   * @returns boolean from status of response.
   */
  validateJwt(code: string, clubId: number = 1) {
    if (this.authService.hasAccessToken()) {
      return of(true);
    }

    let loginUrlParameters: InputField[] = [];

    let codeParam: InputField = {
      FieldName: 'code',
      Value: code,
    };

    loginUrlParameters.push(codeParam);

    let ssoModeParam: InputField = {
      FieldName: 'State',
      Value: this.getSsoState(),
    };
    loginUrlParameters.push(ssoModeParam);

    return this.api.post(`sso/v2/oauth/${ProviderName.FusionAuth}/${clubId}`, loginUrlParameters).pipe(
      tap((authResponse: any) => {
        if (authResponse) {
          this.authService.loginUsingToken(authResponse.tokens.AccessToken, authResponse.tokens.RefreshToken, '/');
          localStorage.setItem('FARefreshToken', authResponse.auth.ProviderRefreshToken.toString());
          localStorage.setItem('FAAccessToken', authResponse.auth.ProviderAccessToken.toString());
        }
      }),
      map((authResponse: any) => {
        if ((authResponse.status = 200)) {
          return true;
        } else {
          return false;
        }
      })
    );
  }

  /**
   * Log out of the portal and provider (FusionAuth).
   * Passes provider tokens and provider ID
   * to logout for the logout endpoint
   * with portal provider details.
   */
  logout() {
    let providerTokens: { [name: string]: string | null } = {};
    providerTokens.providerRefreshToken = localStorage.getItem('FARefreshToken');
    providerTokens.providerAccessToken = localStorage.getItem('FAAccessToken');
    localStorage.removeItem('FARefreshToken');
    localStorage.removeItem('FAAccessToken');

    return this.authService.logout(ProviderName.FusionAuth as any, providerTokens);
  }

  /**
   * This method is used to authenticate a domain
   * token used from the _fa_sso_upid cookie given
   * from FusionAuth domain and log the user
   * using tokens returned.
   * @param domainCookie domain cookie to be
   * authenticated and passed to endpoint.
   * @param clubId clubId to be passed to endpoint,
   * default to 1.
   * @returns boolean from status of response.
   */
  loginUsingToken(domainCookie: string, clubId: number = 1) {
    let faDomainCookie = '';
    if (domainCookie != '' && domainCookie != undefined) {
      faDomainCookie = domainCookie;
    }

    return this.api.get(`sso/OAuth/${ProviderName.FusionAuth}/${faDomainCookie}/${clubId}`).pipe(
      tap((authResponse: any) => {
        if (authResponse) {
          this.authService.loginUsingToken(authResponse.tokens.AccessToken, authResponse.tokens.RefreshToken, '/');
          localStorage.setItem('FARefreshToken', authResponse.auth.RefreshToken.toString());
          localStorage.setItem('FAAccessToken', authResponse.auth.AccessToken.toString());
        }
      }),
      map((authResponse: any) => {
        if ((authResponse.status = 200)) {
          return true;
        } else {
          return false;
        }
      })
    );
  }

  /**
   * Check if a named cookie exists.
   * @param cookieName cookie name to be checked.
   * @returns boolean value depending if cookie name
   * exists.
   */
  hasNamedCookie(cookieName: string): boolean {
    if (this.cookieService.check(cookieName)) {
      return true;
    }
    return false;
  }

  getSsoState(): string {
    return this.cookieService.get(FUSION_SSO_STATE_COOKIE);
  }
}
